import { Button, Checkbox, DatePicker, Form, Select, Tooltip } from "antd";
import dayjs from "dayjs";
import { Copy, MapPin, Package, RotateCcw, ArrowRightLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import type { FormValues, ServiceOverride, TBookingPreview } from "./types";
import { FormProps } from "antd";

interface CopyOptionsFormProps {
  selectedBooking: TBookingPreview | undefined;
  onSubmit: FormProps<FormValues>["onFinish"];
  isSubmitting: boolean;
}

const RETURN_SWAP: Record<string, string> = {
  COLLECTION: "Delivery",
  DELIVERY: "Collection",
};

export const CopyOptionsForm = ({
  selectedBooking,
  onSubmit,
  isSubmitting,
}: CopyOptionsFormProps) => {
  const { data: vessels } = useGetVesselsForDropdown();

  const allServiceIds =
    selectedBooking?.services.map((s) => s.id) || [];
  const [selectedServiceIds, setSelectedServiceIds] =
    useState<string[]>([]);
  const [overrides, setOverrides] = useState<
    Record<string, ServiceOverride>
  >({});
  const [isReturn, setIsReturn] = useState(false);

  useEffect(() => {
    if (allServiceIds.length > 0 && selectedServiceIds.length === 0) {
      setSelectedServiceIds(allServiceIds);
    }
  }, [allServiceIds.length]);

  const allSelected =
    allServiceIds.length > 0 &&
    selectedServiceIds.length === allServiceIds.length;

  const toggleService = (id: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    setSelectedServiceIds(allSelected ? [] : allServiceIds);
  };

  const updateOverride = (
    serviceId: string,
    field: keyof ServiceOverride,
    value: string | undefined
  ) => {
    setOverrides((prev) => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], [field]: value },
    }));
  };

  const handleSubmit: FormProps<FormValues>["onFinish"] = (values) => {
    onSubmit?.({
      ...values,
      isReturn,
      serviceTicketIds: selectedServiceIds,
      serviceTicketOverrides: overrides,
    });
  };

  const vesselOptions = [
    { value: "", label: "No vehicle" },
    ...(vessels?.map((v) => ({ value: v.id, label: v.name })) || []),
  ];

  const getDisplayType = (service: TBookingPreview["services"][0]) => {
    if (isReturn && RETURN_SWAP[service.serviceType]) {
      return RETURN_SWAP[service.serviceType];
    }
    return service.type;
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit}>
      {/* Header with booking info + options */}
      {selectedBooking && (
        <div className="bg-white rounded-lg border p-6 mb-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-medium mb-1">
                Duplicate Booking: {selectedBooking.referenceNumber}
              </h2>
              <p className="text-gray-500 text-sm">
                {selectedBooking.client}
                {selectedBooking.project && ` • ${selectedBooking.project}`}
                {` • ${selectedBooking.date}`}
              </p>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <Form.Item
                name="copyClient"
                valuePropName="checked"
                className="mb-0"
                initialValue={true}
              >
                <Checkbox>Copy Client</Checkbox>
              </Form.Item>
              <Form.Item
                name="copyInventory"
                valuePropName="checked"
                className="mb-0"
                initialValue={true}
              >
                <Checkbox>Copy Inventory</Checkbox>
              </Form.Item>
              <Tooltip title="Swap Collections ↔ Deliveries for return trips">
                <button
                  type="button"
                  onClick={() => setIsReturn(!isReturn)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                    isReturn
                      ? "bg-amber-50 border-amber-500 text-amber-700"
                      : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <RotateCcw size={14} />
                  Return
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      {/* Services to duplicate */}
      {selectedBooking && selectedBooking.services.length > 0 && (
        <div className="bg-white rounded-lg border p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Services to Duplicate</h2>
            <button
              type="button"
              onClick={toggleAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          <div className="space-y-3">
            {selectedBooking.services.map((service) => {
              const isChecked = selectedServiceIds.includes(service.id);
              const displayType = getDisplayType(service);
              const isSwapped = isReturn && RETURN_SWAP[service.serviceType];

              return (
                <div
                  key={service.id}
                  className={`rounded-lg border transition-colors ${
                    isChecked
                      ? "bg-blue-50 border-blue-300"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  {/* Service header row */}
                  <div
                    onClick={() => toggleService(service.id)}
                    className="flex items-center justify-between p-3 cursor-pointer"
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox checked={isChecked} />
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{displayType}</span>
                        {isSwapped && (
                          <span className="inline-flex items-center text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                            <ArrowRightLeft size={10} className="mr-1" />
                            was {service.type}
                          </span>
                        )}
                      </div>
                      {service.location && (
                        <span className="text-gray-500 text-sm inline-flex items-center">
                          <MapPin size={12} className="mr-1" />
                          {service.location}
                        </span>
                      )}
                    </div>
                    {service.inventoryCount > 0 && (
                      <span className="text-sm text-gray-500 inline-flex items-center">
                        <Package size={12} className="mr-1" />
                        {service.inventoryCount} items
                      </span>
                    )}
                  </div>

                  {/* Date & Vessel selectors */}
                  {isChecked && (
                    <div className="px-3 pb-3 pt-0 flex gap-3 ml-9">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-1 block">
                          Service Date
                        </label>
                        <DatePicker
                          className="w-full"
                          size="small"
                          showTime={{
                            format: "h:mm A",
                            use12Hours: true,
                            minuteStep: 15,
                            showSecond: false,
                          }}
                          format="YYYY-MM-DD h:mm A"
                          defaultValue={dayjs()}
                          onChange={(date) =>
                            updateOverride(
                              service.id,
                              "serviceDatetime",
                              date?.toISOString()
                            )
                          }
                          placeholder="Select date & time"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-1 block">
                          Vehicle
                        </label>
                        <Select
                          className="w-full"
                          size="small"
                          defaultValue={service.vesselId || ""}
                          onChange={(value) =>
                            updateOverride(
                              service.id,
                              "vesselId",
                              value || undefined
                            )
                          }
                          options={vesselOptions}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <Button
          htmlType="submit"
          size="large"
          type="primary"
          disabled={!selectedBooking}
          loading={isSubmitting}
          className="px-4 py-2 rounded-lg flex items-center"
        >
          <Copy size={16} className="mr-2" />
          {isReturn ? "Create Return Booking" : "Duplicate Booking"}
        </Button>
      </div>
    </Form>
  );
};
